import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  AlertTriangle, MapPin, Clock, Eye, Plus, Settings, 
  CreditCard, LogOut, User, Bell, Crown, CheckCircle, Menu, X,
  LayoutDashboard, FileText, Users, Lock, Shield, ShieldCheck, Receipt
} from 'lucide-react';
import axios from 'axios';
import NotificationBell from '../components/NotificationBell';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";

export default function DashboardPage() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [myCases, setMyCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [myWarnings, setMyWarnings] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [markFoundLoading, setMarkFoundLoading] = useState(null);

  const handleMarkAsFound = async (caseId, fullName) => {
    if (!window.confirm(`Mark "${fullName}" as Found Safe? This will close the case and remove it from active listings.`)) return;
    setMarkFoundLoading(caseId);
    try {
      await axios.patch(`${API}/cases/${caseId}/mark-found`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      fetchMyCases();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to mark case as found');
    } finally {
      setMarkFoundLoading(null);
    }
  };

  useEffect(() => {
    fetchMyCases();
    fetchSubscriptionStatus();
    fetchMyWarnings();
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const fetchMyWarnings = async () => {
    try {
      const response = await axios.get(`${API}/my-warnings`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setMyWarnings(response.data || []);
    } catch (error) {
      console.error('Failed to fetch warnings:', error);
    }
  };

  const handleSelfDelete = async (caseId) => {
    if (!window.confirm('Permanently delete this case? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/cases/${caseId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      fetchMyCases();
      fetchMyWarnings();
    } catch (error) {
      alert(error?.response?.data?.detail || 'Failed to delete case');
    }
  };

  const formatCountdown = (deleteAtStr) => {
    if (!deleteAtStr) return null;
    const diff = new Date(deleteAtStr).getTime() - now;
    if (diff <= 0) return 'Deadline reached — moderator may now remove this case.';
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    return `${hours}h ${minutes}m remaining to address this warning`;
  };

  const fetchMyCases = async () => {
    try {
      const response = await axios.get(`${API}/my-cases`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setMyCases(response.data);
    } catch (error) {
      console.error('Failed to fetch cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/payments/user-subscription`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    }
  };

  // Check if user can view photos (trial active OR subscription active)
  const canViewPhotos = () => {
    if (!subscriptionStatus) return true; // Loading state - show photos
    return subscriptionStatus.trial_active || subscriptionStatus.subscription_active;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Active', class: 'bg-red-100 text-red-700 border-red-200' },
      under_investigation: { label: 'Under Investigation', class: 'bg-amber-100 text-amber-700 border-amber-200' },
      sighting_confirmed: { label: 'Sighting Confirmed', class: 'bg-blue-100 text-blue-700 border-blue-200' },
      located_safe: { label: 'Found Safe', class: 'bg-green-100 text-green-700 border-green-200' },
      closed: { label: 'Closed', class: 'bg-slate-100 text-slate-700 border-slate-200' }
    };
    const config = statusConfig[status] || statusConfig.active;
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  const getPlanBadge = () => {
    if (user?.subscription_plan === 'premium') {
      return <Badge className="bg-purple-100 text-purple-700">Premium</Badge>;
    } else if (user?.subscription_plan === 'standard') {
      return <Badge className="bg-blue-100 text-blue-700">Standard</Badge>;
    }
    return <Badge className="bg-slate-100 text-slate-700">Free Trial</Badge>;
  };

  const getRoleBadge = () => {
    const roleConfig = {
      admin: { label: 'Admin', class: 'bg-red-100 text-red-700 border-red-200', icon: Shield },
      moderator: { label: 'Moderator', class: 'bg-purple-100 text-purple-700 border-purple-200', icon: ShieldCheck },
      authority: { label: 'Authority', class: 'bg-blue-100 text-blue-700 border-blue-200', icon: Shield },
      user: { label: 'User', class: 'bg-slate-100 text-slate-600 border-slate-200', icon: User }
    };
    const config = roleConfig[user?.role] || roleConfig.user;
    const IconComponent = config.icon;
    return (
      <Badge className={`${config.class} flex items-center gap-1`}>
        <IconComponent className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile Sidebar Toggle */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-200">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="MISYOU" className="w-10 h-10 rounded-xl object-cover" />
              <span className="text-xl font-black text-slate-900">MISYOU</span>
            </Link>
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                ) : (
                  <User className="w-5 h-5 text-primary" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              {getRoleBadge()}
              {getPlanBadge()}
              {user?.verified && (
                <Badge className="bg-green-100 text-green-700">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Verified
                </Badge>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-primary/10 text-primary font-medium">
              <LayoutDashboard className="w-5 h-5" />
              Dashboard
            </Link>
            <Link to="/report" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100">
              <AlertTriangle className="w-5 h-5" />
              Report Missing
            </Link>
            <Link to="/search" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100">
              <Eye className="w-5 h-5" />
              Search Cases
            </Link>
            <Link to="/subscription" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100">
              <CreditCard className="w-5 h-5" />
              Subscription
            </Link>
            <Link to="/my-subscriptions" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100" data-testid="nav-my-subscriptions">
              <Receipt className="w-5 h-5" />
              My Payments &amp; Invoices
            </Link>
            {(user?.role === 'admin' || user?.role === 'moderator') && (
              <>
                <Link to="/moderator" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100">
                  <ShieldCheck className="w-5 h-5" />
                  Moderator Panel
                </Link>
                <Link to="/admin/moderation" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100" data-testid="nav-moderation-cockpit">
                  <AlertTriangle className="w-5 h-5" />
                  Safety Cockpit
                </Link>
              </>
            )}
            {user?.role === 'admin' && (
              <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100">
                <Settings className="w-5 h-5" />
                Admin Panel
              </Link>
            )}
            <Link
              to="/settings/privacy"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-600 hover:bg-slate-100"
              data-testid="privacy-settings-link"
            >
              <ShieldCheck className="w-5 h-5" />
              Privacy &amp; my data
            </Link>
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-slate-200">
            <Button 
              variant="ghost" 
              className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 lg:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
              <p className="text-muted-foreground mt-1">Manage your cases and track their status</p>
            </div>
            <NotificationBell />
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="bg-white shadow-lg shadow-slate-200/50 border-slate-100">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">My Cases</p>
                    <p className="text-3xl font-bold text-slate-900">{myCases.length}</p>
                  </div>
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg shadow-slate-200/50 border-slate-100">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Cases</p>
                    <p className="text-3xl font-bold text-red-600">
                      {myCases.filter(c => c.status === 'active').length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg shadow-slate-200/50 border-slate-100">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Found Safe</p>
                    <p className="text-3xl font-bold text-green-600">
                      {myCases.filter(c => c.status === 'located_safe').length}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Moderator Warnings Banner */}
          {myWarnings.length > 0 && (
            <Card className="mb-8 bg-red-50 border-red-200" data-testid="my-warnings-banner">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-red-800 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Action Required: Moderator Warning ({myWarnings.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {myWarnings.map((w) => (
                  <div
                    key={w.case_id}
                    className="bg-white border border-red-200 rounded-lg p-4"
                    data-testid={`warning-card-${w.case_id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900">{w.full_name}</p>
                        <p className="text-sm text-slate-600 mt-1">
                          <strong>Reason:</strong> {w.warning_message}
                        </p>
                        <p className="text-xs text-red-700 font-medium mt-2">
                          {formatCountdown(w.delete_at)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Address the warning by editing your case, or delete it yourself below.
                          If unresolved, a moderator may remove it after the deadline.
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 shrink-0">
                        <Link to={`/case/${w.case_id}`}>
                          <Button size="sm" variant="outline" className="w-full" data-testid={`warning-view-btn-${w.case_id}`}>
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                        </Link>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50 w-full"
                          onClick={() => handleSelfDelete(w.case_id)}
                          data-testid={`warning-self-delete-btn-${w.case_id}`}
                        >
                          Delete Now
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Grace-window Upgrade Banner — fires when ANY case is blurred and at risk of auto-delete */}
          {(() => {
            const subActive = subscriptionStatus?.subscription_active;
            const graceCases = (myCases || []).filter(c => c.lifecycle_state === 'grace');
            if (subActive || graceCases.length === 0) return null;
            const earliestSeconds = Math.min(...graceCases.map(c => c.grace_seconds_remaining || 0));
            const earliestDays = Math.max(0, Math.ceil(earliestSeconds / 86400));
            const minTotal = 30 * 20; // ₹20/day standard plan × 30 days
            return (
              <Card
                className="mb-8 border-2 border-red-300 bg-gradient-to-r from-red-50 via-amber-50 to-red-50 shadow-lg shadow-red-100/50 animate-pulse-subtle"
                data-testid="grace-upgrade-banner"
              >
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center shrink-0 ring-4 ring-red-50">
                        <Crown className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-bold text-red-900 text-lg">
                            Unlock all my cases instantly
                          </h4>
                          <Badge className="bg-red-600 text-white">
                            {graceCases.length} {graceCases.length === 1 ? 'case' : 'cases'} hidden
                          </Badge>
                        </div>
                        <p className="text-sm text-red-800 mt-1">
                          Your case{graceCases.length === 1 ? "'s" : 's'} {graceCases.length === 1 ? 'photo is' : 'photos are'} blurred and will be{' '}
                          <strong>auto-deleted in {earliestDays} day{earliestDays === 1 ? '' : 's'}</strong>.
                          Subscribe once to restore visibility for <strong>all your active cases</strong>.
                        </p>
                        <p className="text-xs text-red-700 mt-2">
                          From <strong>₹{minTotal}</strong> for 30 days (Standard) — covers every case you've reported.
                        </p>
                      </div>
                    </div>
                    <Link to="/subscription?source=grace_banner" className="shrink-0">
                      <Button
                        size="lg"
                        className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6 shadow-lg shadow-red-200 w-full md:w-auto"
                        data-testid="grace-upgrade-cta"
                      >
                        <Crown className="w-4 h-4 mr-2" />
                        Subscribe & Unlock
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Subscription Warning Banner */}
          {subscriptionStatus && !canViewPhotos() && (
            <Card className="mb-8 bg-amber-50 border-amber-200">
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-amber-800">Free Trial Expired</h4>
                    <p className="text-sm text-amber-700">Subscribe to keep your case photos visible to the community</p>
                  </div>
                </div>
                <Link to="/subscription">
                  <Button className="bg-amber-600 hover:bg-amber-700 text-white rounded-full">
                    <Crown className="w-4 h-4 mr-2" />
                    Subscribe Now
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Report New Case CTA */}
          <Card className="mb-8 bg-gradient-to-r from-primary to-blue-600 text-white shadow-xl">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold">Report a Missing Person</h3>
                <p className="text-blue-100 mt-1">Every second counts. Report now to get help from our community.</p>
              </div>
              <Link to="/report">
                <Button 
                  size="lg" 
                  className="bg-white text-primary hover:bg-white/90 rounded-full px-6 btn-press"
                  data-testid="dashboard-report-btn"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Report Now
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* My Cases */}
          <Card className="bg-white shadow-lg shadow-slate-200/50 border-slate-100">
            <CardHeader className="border-b border-slate-100">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl font-bold">My Reported Cases</CardTitle>
                <Link to="/report">
                  <Button size="sm" className="rounded-full">
                    <Plus className="w-4 h-4 mr-1" />
                    New Case
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mx-auto"></div>
                </div>
              ) : myCases.length === 0 ? (
                <div className="p-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-muted-foreground">You haven't reported any cases yet</p>
                  <Link to="/report">
                    <Button className="mt-4 rounded-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Report Your First Case
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {myCases.map((caseItem) => {
                    const blurred = caseItem.photos_visible === false;
                    const inGrace = caseItem.lifecycle_state === 'grace';
                    const graceDays = inGrace
                      ? Math.max(0, Math.ceil((caseItem.grace_seconds_remaining || 0) / 86400))
                      : null;
                    const isFound = caseItem.status === 'located_safe';
                    return (
                      <div
                        key={caseItem.case_id}
                        className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
                        data-testid={`my-case-${caseItem.case_id}`}
                      >
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-200 shrink-0 relative">
                          {caseItem.photos && caseItem.photos.length > 0 ? (
                            <>
                              <img
                                src={caseItem.photos[0]}
                                alt={caseItem.full_name}
                                className={`w-full h-full object-cover ${blurred ? 'blur-lg' : ''}`}
                              />
                              {blurred && (
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                  <Lock className="w-5 h-5 text-white" />
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <User className="w-8 h-8 text-slate-400" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-semibold text-slate-900 truncate">{caseItem.full_name}</h3>
                            {getStatusBadge(caseItem.status)}
                            {inGrace && (
                              <Badge className="bg-amber-100 text-amber-700">
                                Auto-deletes in {graceDays}d
                              </Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            {caseItem.age && <span>Age: {caseItem.age}</span>}
                            {caseItem.last_seen_location?.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {caseItem.last_seen_location.city}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {caseItem.sightings_count} sightings
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2 shrink-0 items-end">
                          {!isFound && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-emerald-700 border-emerald-200 hover:bg-emerald-50 w-32"
                              onClick={() => handleMarkAsFound(caseItem.case_id, caseItem.full_name)}
                              disabled={markFoundLoading === caseItem.case_id}
                              data-testid={`mark-found-btn-${caseItem.case_id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              {markFoundLoading === caseItem.case_id ? 'Saving…' : 'Mark as Found'}
                            </Button>
                          )}
                          <Link to={`/case/${caseItem.case_id}`}>
                            <Button size="sm" variant="ghost" className="w-32" data-testid={`view-case-btn-${caseItem.case_id}`}>
                              <Eye className="w-4 h-4 mr-1" /> View
                            </Button>
                          </Link>
                          <p className="text-xs text-muted-foreground">{formatDate(caseItem.created_at)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
